import type { AdminTicket } from "@sd/types/admin";
import {
  mapRawToAdminTicket,
  normalizeAdminStatus,
} from "@sd/lib/ticketRowMapping";
import {
  isRetiredTicketStatus,
  isTicketStatus,
  normalizeStatus,
  statusToSlug,
  type TicketStatus,
} from "@sd/constants/ticketStatuses";

export interface KanbanApiData {
  tickets: AdminTicket[];
  /** Per-column counts from API (`counts`, `status_counts`, or group lengths). */
  columnCounts: Partial<Record<TicketStatus, number>>;
  /** Board total from API (`Total`, `total`, etc.). */
  total?: number;
}

const KANBAN_META_SKIP_KEYS = new Set([
  "success",
  "message",
  "code",
  "meta",
  "timezone",
  "tenant",
  "start_date",
  "end_date",
  "project",
  "filters",
  "filter",
  "total",
  "total_count",
  "count",
  "total_tickets",
  "grand_total",
  "counts",
  "status_counts",
  "column_counts",
  "ticket_counts",
]);

const KANBAN_LIST_KEYS = [
  "tickets",
  "grid_data",
  "results",
  "data",
  "items",
  "records",
  "rows",
  "list",
  "content",
];

function readCountValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return Math.trunc(n);
  }
  return undefined;
}

const KANBAN_TOTAL_KEYS = new Set([
  "total",
  "total_count",
  "count",
  "total_tickets",
  "grand_total",
]);

function readExplicitTotal(record: Record<string, unknown>): number | undefined {
  for (const [key, value] of Object.entries(record)) {
    if (KANBAN_TOTAL_KEYS.has(statusToSlug(key))) {
      return readCountValue(value);
    }
  }
  return undefined;
}

function isColumnPayload(value: unknown): boolean {
  if (Array.isArray(value)) return true;
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    Array.isArray(record.items) ||
    Array.isArray(record.tickets) ||
    Array.isArray(record.data) ||
    Array.isArray(record.results) ||
    Array.isArray(record.rows) ||
    Array.isArray(record.list) ||
    isGroupedStatusBoard(record)
  );
}

function extractColumnPayload(value: unknown): {
  items: unknown[];
  count?: number;
} {
  if (Array.isArray(value)) {
    return { items: value };
  }
  if (!value || typeof value !== "object") {
    return { items: [] };
  }
  const record = value as Record<string, unknown>;
  const nested =
    record.items ??
    record.tickets ??
    record.data ??
    record.results ??
    record.rows ??
    record.list;
  if (!Array.isArray(nested)) {
    return { items: [] };
  }
  const count = readCountValue(
    record.count ?? record.total ?? record.total_count ?? record.length
  );
  return { items: nested, count };
}

function isGroupedStatusBoard(record: Record<string, unknown>): boolean {
  const keys = Object.keys(record).filter(
    key => !KANBAN_META_SKIP_KEYS.has(statusToSlug(key))
  );
  if (keys.length === 0) return false;
  return keys.every(key => isColumnPayload(record[key]));
}

/**
 * Map API count keys to kanban columns.
 * Exact workflow statuses only — never alias retired keys (e.g. `fixed`)
 * into Ready for QA via normalizeAdminStatus (that can overwrite the real count).
 */
function resolveKanbanCountStatus(raw: unknown): TicketStatus | null {
  if (raw === null || raw === undefined) return null;
  const slug = statusToSlug(raw);
  if (!slug || isRetiredTicketStatus(slug)) return null;
  if (isTicketStatus(slug)) return slug;
  return null;
}

function parseExplicitColumnCounts(
  record: Record<string, unknown>
): Partial<Record<TicketStatus, number>> {
  const countsRoot =
    record.counts ??
    record.status_counts ??
    record.column_counts ??
    record.ticket_counts;

  if (!countsRoot || typeof countsRoot !== "object" || Array.isArray(countsRoot)) {
    return {};
  }

  const out: Partial<Record<TicketStatus, number>> = {};
  for (const [key, value] of Object.entries(
    countsRoot as Record<string, unknown>
  )) {
    const status = resolveKanbanCountStatus(key);
    const count = readCountValue(value);
    if (status && count !== undefined) out[status] = count;
  }
  return out;
}

function ticketStatusFromGroupedItem(
  item: unknown,
  bucketStatus: TicketStatus
): TicketStatus {
  const raw = (item ?? {}) as Record<string, unknown>;
  const statusRaw = raw.status ?? raw.ticket_status ?? raw.ticketStatus;
  if (
    statusRaw !== undefined &&
    statusRaw !== null &&
    String(statusRaw).trim() !== ""
  ) {
    return normalizeAdminStatus(statusRaw);
  }
  return bucketStatus;
}

function tallyColumnCounts(
  tickets: AdminTicket[]
): Partial<Record<TicketStatus, number>> {
  const out: Partial<Record<TicketStatus, number>> = {};
  for (const t of tickets) {
    const status = normalizeStatus(t.status);
    out[status] = (out[status] ?? 0) + 1;
  }
  return out;
}

function parseGroupedStatusBoard(
  record: Record<string, unknown>
): { tickets: AdminTicket[]; columnCounts: Partial<Record<TicketStatus, number>> } {
  const tickets: AdminTicket[] = [];

  for (const [key, value] of Object.entries(record)) {
    if (KANBAN_META_SKIP_KEYS.has(statusToSlug(key))) continue;

    const bucketStatus = normalizeAdminStatus(key);
    const nestedBoard =
      value && typeof value === "object" && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;

    if (nestedBoard && isGroupedStatusBoard(nestedBoard)) {
      tickets.push(...parseGroupedStatusBoard(nestedBoard).tickets);
      continue;
    }

    const { items } = extractColumnPayload(value);

    for (const item of items) {
      const t = mapRawToAdminTicket(item);
      tickets.push({
        ...t,
        status: ticketStatusFromGroupedItem(item, bucketStatus),
      });
    }
  }

  return { tickets, columnCounts: tallyColumnCounts(tickets) };
}

function parseKanbanListKeyValue(
  value: unknown,
  explicitCounts: Partial<Record<TicketStatus, number>>,
  total: number | undefined
): KanbanApiData | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return {
      tickets: value.map(mapRawToAdminTicket),
      columnCounts: explicitCounts,
      total,
    };
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const inner = value as Record<string, unknown>;
  if (isGroupedStatusBoard(inner)) {
    const parsed = parseGroupedStatusBoard(inner);
    return {
      tickets: parsed.tickets,
      columnCounts: { ...parsed.columnCounts, ...explicitCounts },
      total,
    };
  }
  const nested = parseKanbanPayload(inner);
  if (nested.tickets.length > 0) {
    return {
      tickets: nested.tickets,
      columnCounts: { ...nested.columnCounts, ...explicitCounts },
      total: total ?? nested.total,
    };
  }
  return null;
}

function parseKanbanPayload(data: unknown): KanbanApiData {
  if (!data || typeof data !== "object") {
    return { tickets: [], columnCounts: {} };
  }

  const record = data as Record<string, unknown>;
  const explicitCounts = parseExplicitColumnCounts(record);
  const total = readExplicitTotal(record);

  if (Array.isArray(data)) {
    const tickets = data.map(mapRawToAdminTicket);
    return { tickets, columnCounts: explicitCounts, total };
  }

  // Prefer known list wrappers (`tickets`, `results`, …) before treating the
  // root object as status columns — `{ tickets: { in_progress: [...] } }` must
  // not be parsed with `tickets` as a workflow status.
  for (const key of KANBAN_LIST_KEYS) {
    const parsed = parseKanbanListKeyValue(
      record[key],
      explicitCounts,
      total
    );
    if (parsed) return parsed;
  }

  if (isGroupedStatusBoard(record)) {
    const parsed = parseGroupedStatusBoard(record);
    return {
      tickets: parsed.tickets,
      columnCounts: { ...parsed.columnCounts, ...explicitCounts },
      total,
    };
  }

  return { tickets: [], columnCounts: explicitCounts, total };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** Unwrap typical API envelopes and axios `response.data`. */
export function parseKanbanApiResponse(body: unknown): KanbanApiData {
  const outer = asRecord(body);
  let payload = body;
  if (outer && "data" in outer) {
    payload = outer.data;
  }
  const parsed = parseKanbanPayload(payload);
  const totalFromOuter = outer ? readExplicitTotal(outer) : undefined;
  return {
    ...parsed,
    total: totalFromOuter ?? parsed.total,
  };
}

/** @deprecated Prefer `parseKanbanApiResponse` for counts + total. */
export function ticketsFromKanbanApiResponse(body: unknown): AdminTicket[] {
  return parseKanbanApiResponse(body).tickets;
}

/** Column badge count — API first, then visible cards in column. */
export function kanbanColumnCount(
  status: TicketStatus,
  columnCounts: Partial<Record<TicketStatus, number>>,
  tickets: AdminTicket[]
): number {
  const fromApi = columnCounts[status];
  if (fromApi !== undefined) return fromApi;
  return tickets.filter(t => normalizeStatus(t.status) === status).length;
}

/** Optimistic Kanban move after drag-and-drop — instant UI before refetch. */
export function moveTicketInKanbanData(
  data: KanbanApiData,
  ticketId: string,
  nextStatus: TicketStatus
): KanbanApiData {
  const current = data.tickets.find(t => t.id === ticketId);
  if (!current) return data;

  const fromStatus = normalizeStatus(current.status);
  if (fromStatus === nextStatus) return data;

  const tickets = data.tickets.map(t =>
    t.id === ticketId ? { ...t, status: nextStatus } : t
  );

  const columnCounts = { ...data.columnCounts };
  if (columnCounts[fromStatus] !== undefined) {
    columnCounts[fromStatus] = Math.max(0, (columnCounts[fromStatus] ?? 0) - 1);
  }
  if (columnCounts[nextStatus] !== undefined) {
    columnCounts[nextStatus] = (columnCounts[nextStatus] ?? 0) + 1;
  }

  return {
    ...data,
    tickets,
    columnCounts:
      Object.keys(columnCounts).length > 0
        ? columnCounts
        : tallyColumnCounts(tickets),
  };
}
